import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Table,
  Dropdown,
  Input,
  Segment,
  Icon,
  Pagination
} from "semantic-ui-react";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { closeModal, openModal } from "../../../app/common/modals/modalSlice";
import {
  formatCommentDate,
  generateCommentCSV,
  downloadCSV
} from "./docCommentsUtils";
import { resolveComment } from "./docCommentsActions";

export default function DocCommentHistoryModal() {
  const dispatch = useDispatch();
  const { comments } = useSelector((state) => state.docComments);
  const { doc } = useSelector((state) => state.doc);
  
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const statusOptions = [
    { key: "all", text: "All Comments", value: "all" },
    { key: "active", text: "Active", value: "active" },
    { key: "resolved", text: "Resolved", value: "resolved" },
    { key: "deleted", text: "Deleted", value: "deleted" },
  ];

  const filteredComments = useMemo(() => {
    let filtered = comments;

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(comment => comment.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(comment => 
        comment.comment.toLowerCase().includes(term) ||
        `${comment.createdBy.firstName} ${comment.createdBy.lastName}`.toLowerCase().includes(term) ||
        comment.createdBy.role.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [comments, statusFilter, searchTerm]);

  const paginatedComments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredComments.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredComments, currentPage]);

  const totalPages = Math.ceil(filteredComments.length / itemsPerPage);



  const handleDownload = () => {
    const csvContent = generateCommentCSV(filteredComments, doc.name);
    const filename = `${doc.name}_comments_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  const handleClose = () => {
    dispatch(closeModal());
  };

  return (
    <ModalWrapper size="large" header={`Comment History - ${doc.name}`}>
      <Segment>
        {/* Filters and Search */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "10px"
        }}>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Dropdown
              placeholder="Filter by status"
              selection
              options={statusOptions}
              value={statusFilter}
              onChange={(e, { value }) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
            />
            <Input
              icon="search"
              placeholder="Search comments..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <Button
            icon="download"
            content="Download CSV"
            onClick={handleDownload}
            disabled={filteredComments.length === 0}
          />
        </div>

        {/* Results Summary */}
        <div style={{ marginBottom: "15px", color: "#666" }}>
          Showing {paginatedComments.length} of {filteredComments.length} comments
        </div>

        {/* Comments Table */}
        {paginatedComments.length > 0 ? (
          <>
            <Table celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Date/Time</Table.HeaderCell>
                  <Table.HeaderCell>Page</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Author</Table.HeaderCell>
                  <Table.HeaderCell>Comment</Table.HeaderCell>
                  <Table.HeaderCell style={{ minWidth: "160px" }}>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {paginatedComments.map((comment) => (
                  <Table.Row key={comment.id}>
                    <Table.Cell>
                      <div>{formatCommentDate(comment.createdAt)}</div>
                      {comment.editedAt && (
                        <div style={{ fontSize: "0.8em", color: "#666" }}>
                          <Icon name="edit" size="small" />
                          Edited: {formatCommentDate(comment.editedAt)}
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>{(comment.pageNumber || 0) + 1}</Table.Cell>
                    <Table.Cell>
                      <span style={{
                        color: comment.status === 'resolved' ? '#21ba45' :
                               comment.status === 'active' ? '#2185d0' : '#666',
                        fontWeight: '500',
                        textTransform: 'capitalize'
                      }}>
                        {comment.status}
                      </span>
                      {comment.resolvedAt && (
                        <div style={{ fontSize: "0.8em", color: "#666", marginTop: "5px" }}>
                          Resolved: {formatCommentDate(comment.resolvedAt)}
                          {comment.resolvedBy && (
                            <div>by {comment.resolvedBy.firstName} {comment.resolvedBy.lastName}</div>
                          )}
                        </div>
                      )}
                      {comment.deletedAt && (
                        <div style={{ fontSize: "0.8em", color: "#666", marginTop: "5px" }}>
                          Deleted: {formatCommentDate(comment.deletedAt)}
                          {comment.deletedBy && (
                            <div>by {comment.deletedBy.firstName} {comment.deletedBy.lastName}</div>
                          )}
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <div>
                        <strong>{comment.createdBy.firstName} {comment.createdBy.lastName}</strong>
                      </div>
                      <div style={{ fontSize: "0.9em", color: "#666" }}>
                        {comment.createdBy.role}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div style={{ maxWidth: "300px", wordWrap: "break-word" }}>
                        {comment.comment}
                      </div>
                    </Table.Cell>
                    <Table.Cell style={{ whiteSpace: "nowrap" }}>
                      <Button
                        size="mini"
                        icon="eye"
                        color="blue"
                        content="View"
                        onClick={() => {
                          dispatch(closeModal());
                          dispatch(openModal({
                            modalType: "DocCommentViewModal",
                            modalProps: { comment }
                          }));
                        }}
                      />
                      {comment.status === 'active' && (
                        <Button
                          size="mini"
                          icon="check"
                          color="green"
                          content="Resolve"
                          style={{ marginLeft: "5px" }}
                          onClick={() => {
                            dispatch(resolveComment(comment.id, comment.documentId));
                          }}
                        />
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                <Pagination
                  activePage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(e, { activePage }) => setCurrentPage(activePage)}
                />
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
            <Icon name="comment outline" size="huge" />
            <div style={{ marginTop: "10px" }}>
              {searchTerm || statusFilter !== "all" 
                ? "No comments match your filters" 
                : "No comments found for this document"}
            </div>
          </div>
        )}

        {/* Close Button */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
          <Button content="Close" onClick={handleClose} />
        </div>
      </Segment>
    </ModalWrapper>
  );
}
